import React, { FC, useState, DragEvent, useMemo, useCallback } from 'react'
import { CatalogColumn, SelectedColumn } from './types'
import { formatColumnHeaderLabel } from './columnHeaderLabel'

interface ColumnChooserDrawerProps {
  isOpen: boolean
  onClose: () => void
  catalogColumns: CatalogColumn[]
  selectedColumns: SelectedColumn[]
  onToggleColumn: (field: string) => void
  onToggleMultipleColumns?: (fields: string[]) => void
  onUncheckAllColumns?: () => void
  onReorderColumns: (dragIndex: number, hoverIndex: number) => void
  onPinColumn?: (field: string, position: 'left' | 'right' | null) => void
  getColumnByField: (field: string) => CatalogColumn | undefined
  mandatoryColumns?: string[]
}

export const ColumnChooserDrawer: FC<ColumnChooserDrawerProps> = ({
  isOpen,
  onClose,
  catalogColumns,
  selectedColumns,
  onToggleColumn,
  onToggleMultipleColumns,
  onUncheckAllColumns,
  onReorderColumns,
  onPinColumn,
  getColumnByField,
  mandatoryColumns = ['product_name', 'linking_status', 'link_actions'],
}) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [draggedField, setDraggedField] = useState<string | null>(null)
  const [dragOverField, setDragOverField] = useState<string | null>(null)

  const getColumnDisplayLabel = (col?: CatalogColumn) => {
    if (!col) return ''
    return formatColumnHeaderLabel(col.headerName || col.label || col.name || col.field || '')
  }

  // Sorted columns for consistent ordering
  const sortedColumns = useMemo(
    () => [...selectedColumns].sort((a, b) => a.order - b.order),
    [selectedColumns]
  )

  const matchesSearch = useCallback((col?: CatalogColumn) => {
    if (!col) return false
    const label = getColumnDisplayLabel(col)
    const field = col.field || ''
    const query = searchQuery.toLowerCase()
    return label.toLowerCase().includes(query) || field.toLowerCase().includes(query)
  }, [searchQuery])

  // Filter catalog columns by search (for available section)
  const filteredCatalog = useMemo(() => {
    return catalogColumns.filter((col) => {
      if (col.allowVisibility === 'NO') return false
      return matchesSearch(col)
    })
  }, [catalogColumns, matchesSearch])

  // Filter visible columns by same search (for visible section)
  const filteredVisibleColumns = useMemo(() => {
    return sortedColumns.filter((selectedCol) => {
      const catalogCol = getColumnByField(selectedCol.field)
      return matchesSearch(catalogCol)
    })
  }, [sortedColumns, getColumnByField, matchesSearch])

  // Check if column is visible
  const isVisible = (field: string) => {
    return selectedColumns.some((col) => col.field === field)
  }

  // All available (non-visible) columns ignoring search
  const allAvailableColumns = useMemo(
    () => catalogColumns.filter((col) => col.allowVisibility !== 'NO' && !isVisible(col.field)),
    [catalogColumns, selectedColumns]
  )

  // Available (non-visible) columns matching search
  const availableColumns = useMemo(
    () => filteredCatalog.filter((col) => !isVisible(col.field)),
    [filteredCatalog, selectedColumns]
  )

  // Check if all available columns are selected
  const allAvailableSelected = allAvailableColumns.length === 0

  // Toggle all available columns
  const handleSelectAll = () => {
    if (allAvailableSelected) {
      onUncheckAllColumns?.()
      return
    }

    // Use batch handler if available, otherwise fall back to individual toggles
    if (onToggleMultipleColumns) {
      const fields = allAvailableColumns.map((col) => col.field)
      onToggleMultipleColumns(fields)
    } else {
      // Fallback: toggle one by one
      allAvailableColumns.forEach((col) => {
        onToggleColumn(col.field)
      })
    }
  }

  // Drag handlers
  const handleDragStart = (e: DragEvent, field: string) => {
    setDraggedField(field)
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/plain', field)
  }

  const handleDragEnter = (e: DragEvent, field: string) => {
    e.preventDefault()
    if (draggedField !== field) {
      setDragOverField(field)
    }
  }

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
  }

  const handleDrop = (e: DragEvent, dropField: string) => {
    e.preventDefault()
    e.stopPropagation()
    
    if (draggedField && draggedField !== dropField) {
      // Find indices in the sorted array
      const dragIndex = sortedColumns.findIndex(col => col.field === draggedField)
      const dropIndex = sortedColumns.findIndex(col => col.field === dropField)
      
      if (dragIndex !== -1 && dropIndex !== -1) {
        onReorderColumns(dragIndex, dropIndex)
      }
    }
    
    setDraggedField(null)
    setDragOverField(null)
  }

  const handleDragEnd = () => {
    setDraggedField(null)
    setDragOverField(null)
  }

  if (!isOpen) return null

  return (
    <>
      {/* Backdrop */}
      <div className='modal-backdrop fade show' onClick={onClose} style={{zIndex: 1049}}></div>

      {/* Drawer */}
      <div
        className='offcanvas offcanvas-end show'
        tabIndex={-1}
        style={{zIndex: 1050, width: '400px'}}
      >
        <div className='offcanvas-header border-bottom'>
          <h5 className='offcanvas-title'>Configure Column</h5>
          <button type='button' className='btn-close' onClick={onClose}></button>
        </div>

        <div className='offcanvas-body p-0 d-flex flex-column'>
          {/* Search */}
          <div className='p-4 border-bottom'>
            <input
              type='text'
              className='form-control'
              placeholder='Search columns...'
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {/* Visible columns section */}
          <div className='flex-fill overflow-auto'>
            <div className='p-4 pb-2'>
              <h6 className='text-muted text-uppercase fs-7 mb-3'>
                Visible Columns ({filteredVisibleColumns.length})
              </h6>

              <div className='d-flex flex-column gap-2'>
                {filteredVisibleColumns.map((selectedCol) => {
                  const catalogCol = getColumnByField(selectedCol.field)
                  if (!catalogCol) return null
                  const isMandatory =
                    mandatoryColumns.includes(selectedCol.field) ||
                    catalogCol.fixed ||
                    catalogCol.allowVisibility === 'ALWAYS_VISIBLE'

                  return (
                    <div
                      key={selectedCol.field}
                      draggable
                      onDragStart={(e) => handleDragStart(e, selectedCol.field)}
                      onDragEnter={(e) => handleDragEnter(e, selectedCol.field)}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, selectedCol.field)}
                      onDragEnd={handleDragEnd}
                      className={`d-flex align-items-center gap-2 p-3 bg-light rounded ${
                        draggedField === selectedCol.field ? 'opacity-50' : ''
                      } ${dragOverField === selectedCol.field ? 'border border-primary' : ''}`}
                      style={{cursor: 'move', transition: 'all 0.2s'}}
                    >
                      {/* Drag handle */}
                      <i className='bi bi-grip-vertical text-muted'></i>

                      {/* Checkbox */}
                      <input
                        type='checkbox'
                        className='form-check-input m-0'
                        checked={true}
                        onChange={() => onToggleColumn(selectedCol.field)}
                        disabled={isMandatory}
                      />

                      {/* Label */}
                      <span className='flex-fill'>
                        {getColumnDisplayLabel(catalogCol)}
                      </span>

                      {/* Pin buttons */}
                      {onPinColumn && (
                        <div className='btn-group btn-group-sm' role='group'>
                          <button
                            type='button'
                            className={`btn btn-sm ${
                              selectedCol.pinned === 'left' ? 'btn-primary' : 'btn-light'
                            }`}
                            onClick={() =>
                              onPinColumn(
                                selectedCol.field,
                                selectedCol.pinned === 'left' ? null : 'left'
                              )
                            }
                            title='Pin to left'
                            style={{padding: '2px 6px', fontSize: '11px'}}
                          >
                            <i
                              className='bi bi-pin-angle-fill'
                              style={{transform: 'rotate(-45deg)'}}
                            ></i>
                          </button>
                          <button
                            type='button'
                            className={`btn btn-sm ${
                              selectedCol.pinned === 'right' ? 'btn-primary' : 'btn-light'
                            }`}
                            onClick={() =>
                              onPinColumn(
                                selectedCol.field,
                                selectedCol.pinned === 'right' ? null : 'right'
                              )
                            }
                            title='Pin to right'
                            style={{padding: '2px 6px', fontSize: '11px'}}
                          >
                            <i
                              className='bi bi-pin-angle-fill'
                              style={{transform: 'rotate(45deg)'}}
                            ></i>
                          </button>
                        </div>
                      )}
                    </div>
                  )
                })}
                {filteredVisibleColumns.length === 0 && (
                  <div className='text-muted small py-2'>No visible columns match your search.</div>
                )}
              </div>
            </div>

            {/* Available columns section */}
            <div className='p-4 pt-2'>
              <div className='d-flex align-items-center justify-content-between mb-3'>
                <h6 className='text-muted text-uppercase fs-7 mb-0'>Available Columns</h6>
                <div className='d-flex align-items-center gap-2'>
                  <input
                    type='checkbox'
                    className='form-check-input m-0'
                    checked={allAvailableSelected}
                    onChange={handleSelectAll}
                    disabled={allAvailableColumns.length === 0 && !onUncheckAllColumns}
                    id='select-all-columns'
                  />
                  <label
                    htmlFor='select-all-columns'
                    className='form-check-label text-muted small mb-0'
                    style={{cursor: 'pointer'}}
                    onClick={(e) => {
                      e.preventDefault()
                      handleSelectAll()
                    }}
                  >
                    {allAvailableSelected ? 'Restore Default Columns' : 'Select All'}
                  </label>
                </div>
              </div>

              <div className='d-flex flex-column gap-2'>
                {availableColumns.map((col) => (
                  <div
                    key={col.field}
                    className='d-flex align-items-center gap-3 p-3 bg-light rounded'
                    role='button'
                    tabIndex={0}
                    onClick={() => onToggleColumn(col.field)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        onToggleColumn(col.field)
                      }
                    }}
                    style={{cursor: 'pointer'}}
                  >
                    {/* Placeholder for alignment */}
                    <div style={{width: '20px'}}></div>

                    {/* Checkbox */}
                    <input
                      type='checkbox'
                      className='form-check-input m-0'
                      checked={false}
                      readOnly
                      onClick={(e) => {
                        e.stopPropagation()
                        onToggleColumn(col.field)
                      }}
                    />

                    {/* Label */}
                      <span className='flex-fill'>
                        {getColumnDisplayLabel(col)}
                      </span>
                  </div>
                ))}
                {availableColumns.length === 0 && (
                  <div className='text-muted small py-2'>No available columns match your search.</div>
                )}
              </div>
            </div>
          </div>

          {/* Footer actions */}
          <div className='p-4 border-top'>
            <button className='btn btn-primary w-100' onClick={onClose}>
              Done
            </button>
          </div>
        </div>
      </div>
    </>
  )
}
